// ===========================================
// CARD TRACKING STORE
// ===========================================
// Tracks all cards seen since last shuffle for
// Hi-Lo card counting and true count calculation.
// ===========================================

import { create } from 'zustand';
import {
  getHiLoValue,
  calculateTrueCount,
  estimateDecksRemaining,
  type CardForCounting,
} from '../utils/basicStrategy';

// ===========================================
// TYPES
// ===========================================

interface CardTrackingState {
  /** All cards seen since last shuffle */
  cardsSeenSinceShuffle: CardForCounting[];
  /** Running count (sum of Hi-Lo values) */
  runningCount: number;
  /** True count (running count / decks remaining) */
  trueCount: number;
  /** Estimated decks remaining */
  decksRemaining: number;
  /** Number of decks in the shoe */
  totalDecks: number;
  /** Server penetration (synced at round end for accuracy) */
  serverPenetration: number;
}

interface CardTrackingActions {
  /** Track a single card */
  trackCard: (card: CardForCounting) => void;
  /** Track multiple cards at once */
  trackCards: (cards: CardForCounting[]) => void;
  /** Reset on shuffle */
  resetOnShuffle: () => void;
  /** Set total decks in shoe */
  setTotalDecks: (decks: number) => void;
  /** Sync penetration from server value (called at round end) */
  syncServerPenetration: (serverPenetration: number) => void;
}

type CardTrackingStore = CardTrackingState & CardTrackingActions;

// ===========================================
// INITIAL STATE
// ===========================================

const DEFAULT_TOTAL_DECKS = 6;

const initialState: CardTrackingState = {
  cardsSeenSinceShuffle: [],
  runningCount: 0,
  trueCount: 0,
  decksRemaining: DEFAULT_TOTAL_DECKS,
  totalDecks: DEFAULT_TOTAL_DECKS,
  serverPenetration: 0,
};

// ===========================================
// STORE
// ===========================================

export const useCardTrackingStore = create<CardTrackingStore>((set, get) => ({
  ...initialState,

  trackCard: (card) => {
    const { cardsSeenSinceShuffle, runningCount, totalDecks } = get();

    // Add card to history
    const newCards = [...cardsSeenSinceShuffle, card];

    // Update running count
    const cardValue = getHiLoValue(card);
    const newRunningCount = runningCount + cardValue;

    // Calculate decks remaining and true count
    const newDecksRemaining = estimateDecksRemaining(newCards.length, totalDecks);
    const newTrueCount = calculateTrueCount(newRunningCount, newDecksRemaining);

    set({
      cardsSeenSinceShuffle: newCards,
      runningCount: newRunningCount,
      decksRemaining: newDecksRemaining,
      trueCount: newTrueCount,
    });
  },

  trackCards: (cards) => {
    const { cardsSeenSinceShuffle, runningCount, totalDecks } = get();

    // Add all cards to history
    const newCards = [...cardsSeenSinceShuffle, ...cards];

    // Update running count for all cards
    let additionalCount = 0;
    for (const card of cards) {
      additionalCount += getHiLoValue(card);
    }
    const newRunningCount = runningCount + additionalCount;

    // Calculate decks remaining and true count
    const newDecksRemaining = estimateDecksRemaining(newCards.length, totalDecks);
    const newTrueCount = calculateTrueCount(newRunningCount, newDecksRemaining);

    set({
      cardsSeenSinceShuffle: newCards,
      runningCount: newRunningCount,
      decksRemaining: newDecksRemaining,
      trueCount: newTrueCount,
    });
  },

  resetOnShuffle: () => {
    const { totalDecks } = get();
    set({
      cardsSeenSinceShuffle: [],
      runningCount: 0,
      trueCount: 0,
      decksRemaining: totalDecks,
      serverPenetration: 0,
    });
    console.log('[CardTracking] Reset on shuffle');
  },

  setTotalDecks: (decks) => {
    set({
      totalDecks: decks,
      decksRemaining: decks,
    });
  },

  syncServerPenetration: (serverPenetration) => {
    set({ serverPenetration });
    console.log('[CardTracking] Synced server penetration:', serverPenetration.toFixed(2) + '%');
  },
}));

// ===========================================
// SELECTORS
// ===========================================

export const selectTrueCount = (state: CardTrackingStore) => state.trueCount;
export const selectRunningCount = (state: CardTrackingStore) => state.runningCount;
export const selectDecksRemaining = (state: CardTrackingStore) => state.decksRemaining;
export const selectCardsDealt = (state: CardTrackingStore) => state.cardsSeenSinceShuffle.length;

/** Penetration percentage (0-100) - uses client calculation with server sync
 *  Client updates per-card, server corrects at round end */
export const selectPenetration = (state: CardTrackingStore) => {
  const totalCards = state.totalDecks * 52;
  const cardsDealt = state.cardsSeenSinceShuffle.length;
  const clientPenetration = totalCards > 0 ? (cardsDealt / totalCards) * 100 : 0;
  // Use max of client and server - client adds per card, server syncs at round end
  return Math.max(clientPenetration, state.serverPenetration);
};
